import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ApiResponse } from "@/shared/types/api.ts";
import { QUERY_KEYS } from "@/machines/api/config.ts";
import { QUERY_KEYS as SETTINGS_QUERY_KEYS } from "@/settings/api/config.ts";
import {
  getKpiStats,
  getMachineById,
  getMachinesByLocation,
  IGetKpiStatsInput,
  IGetKpiStatsResponse,
  IGetMachineByIdResponse,
  IGetMachinesResponse,
  updateMachineSettings,
  IUpdateMachineSettingsInput,
} from "@/machines/api/requests/machines.requests.ts";
import { Machine } from "@/machines/types/machine.ts";

export const useGetMachinesByLocation = (locationId?: number | string) => {
  return useQuery<ApiResponse<IGetMachinesResponse[]>>({
    queryKey: [QUERY_KEYS.MACHINES, {locationId: locationId}],
    queryFn: () => getMachinesByLocation(locationId),
    enabled: !!locationId,
    staleTime:  1000 * 60 * 60 * 12, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};

export const useGetMachineById = (id: Machine["id"]) => {
  return useQuery<ApiResponse<IGetMachineByIdResponse>>({
    queryKey: [QUERY_KEYS.MACHINES, id],
    queryFn: () => getMachineById(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  });
};

export const useGetKpiStats = (params: IGetKpiStatsInput) => {

  return useQuery<ApiResponse<IGetKpiStatsResponse>>({
    queryKey: [QUERY_KEYS.KPI_STATS, params],
    queryFn: () => getKpiStats(params),
    enabled: !!params && !!params.device_mxid,
    staleTime:  1000 * 60 * 60 * 12, // 5 minutes - data considered fresh
    gcTime: 1000 * 60 * 60 * 24, // 24 hours - keep in cache
  })
}

export const useUpdateMachineSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateMachineSettings,
    onSuccess: (data, variables) => {
      // Invalidate and refetch machine queries
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MACHINES, variables.id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MACHINES] });
      // Invalidate settings queries so the settings page reflects updated data
      queryClient.invalidateQueries({ queryKey: [SETTINGS_QUERY_KEYS.SETTINGS] });
    },
  });
};